import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react'
import { deleteUser } from 'aws-amplify/auth'
import { APP_PATH, authConfigured, socialProviders } from '../amplify'
import { deleteAllUserData } from '../lib/storage'
import { clearLocal } from '../lib/persist'
import { clearOffline, offlineAvailable } from '../lib/offline'
import { RUNNING_VERSION, lastCheck, liveUpdateAvailable } from '../lib/liveUpdate'
import { isNativeApp } from '../lib/native'

/** My Account: sign in / sign up, sign out, and account deletion (required by the App Store). */
export default function Account() {
  if (!authConfigured) {
    return (
      <main className={`landing${isNativeApp ? ' landing-app' : ''}`}>
        <h1>My Account</h1>
        <p className="muted">Accounts are not enabled in this build.</p>
        <Link className="button secondary small-button" to="/">
          ← Home
        </Link>
      </main>
    )
  }
  return <AccountInner />
}

function AccountInner() {
  const { authStatus, user, signOut } = useAuthenticator((ctx) => [ctx.authStatus, ctx.user])
  const navigate = useNavigate()
  // Opened from Race Studio's Account button: the back button returns there instead of home.
  const fromStudio = (useLocation().state as { from?: string } | null)?.from === 'studio'
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const email = user?.signInDetails?.loginId ?? user?.username ?? ''

  /** The offline copy of the repo belongs to the account, so it goes with the sign-out. */
  async function onSignOut() {
    if (offlineAvailable) await clearOffline()
    signOut()
  }

  async function deleteAccount() {
    setWorking(true)
    setError(null)
    try {
      // Files and notes in the repo first, then the user itself; local caches last.
      await deleteAllUserData()
      await deleteUser()
      clearLocal()
      if (offlineAvailable) await clearOffline()
      navigate('/')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setWorking(false)
    }
  }

  return (
    <main className={`landing${isNativeApp ? ' landing-app' : ''} account`}>
      <header className="landing-head">
        <div>
          <h1>My Account</h1>
          <p className="tagline">Mala Racing</p>
        </div>
        <Link className="button secondary small-button" to={fromStudio ? APP_PATH : '/'}>
          {fromStudio ? '← Race Studio' : '← Home'}
        </Link>
      </header>

      {authStatus !== 'authenticated' && (
        <section className="panel">
          <p className="muted small">Sign in or create a free account to keep your sessions in your own repo and open them on any device.</p>
          <Authenticator loginMechanisms={['email']} signUpAttributes={['email']} socialProviders={socialProviders} />
        </section>
      )}

      {authStatus === 'authenticated' && (
        <>
          <section className="panel account-row">
            <div>
              <p className="muted small">Signed in as</p>
              <p className="account-email">{email}</p>
            </div>
            <button className="button secondary" onClick={() => void onSignOut()}>
              Sign out
            </button>
          </section>

          <section className="panel">
            <h3>Delete my account</h3>
            <p className="muted small">
              This permanently deletes your account, every session file and note in your repo, and the saved workspace. Files
              downloaded to this device are also forgotten. This cannot be undone.
            </p>
            {!confirming && (
              <button className="button danger-button" onClick={() => setConfirming(true)}>
                Delete my account…
              </button>
            )}
            {confirming && (
              <div className="account-confirm">
                <label className="small">
                  Type <strong>DELETE</strong> to confirm
                  <input className="note" type="text" value={typed} onChange={(e) => setTyped(e.target.value)} autoCapitalize="characters" />
                </label>
                <div className="side-row">
                  <button className="button danger-button" disabled={typed.trim() !== 'DELETE' || working} onClick={() => void deleteAccount()}>
                    {working ? 'Deleting…' : 'Delete everything'}
                  </button>
                  <button className="button secondary" disabled={working} onClick={() => setConfirming(false)}>
                    Keep my account
                  </button>
                </div>
              </div>
            )}
            {error && <p className="error">{error}</p>}
          </section>
        </>
      )}

      <p className="muted small account-links">
        <Link to="/privacy">Privacy policy</Link> · <Link to="/support">Support</Link>
      </p>
      {liveUpdateAvailable && <VersionLine />}
    </main>
  )
}

/** Which web build the app is running and what the last update check did. */
function VersionLine() {
  const last = lastCheck()
  const what =
    last == null
      ? 'not checked yet'
      : last.result === 'none'
        ? 'up to date'
        : last.result === 'switched' || last.result === 'downloaded'
          ? `update ${last.version?.slice(0, 7)} ${last.result}`
          : `${last.result}${last.error ? `: ${last.error}` : ''}`
  return (
    <p className="muted small account-links">
      Build {RUNNING_VERSION.slice(0, 7)} · {what}
      {last ? ` (${new Date(last.at).toLocaleTimeString()})` : ''}
    </p>
  )
}
